import React, { useState } from "react";
import { faqRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Badge, Button, DataTable, Input, Modal, PageHead, Textarea, useAsync, useTableState } from "../../components/ui";

const EMPTY = { id: "", question: "", answer: "", category: "" };

// จัดการคำถามที่พบบ่อย — แสดงในศูนย์ช่วยเหลือฝั่งลูกค้า (/app/help)
export default function Faqs() {
  const { orgId, toast } = useApp();
  const { tx } = useI18n();
  const tableState = useTableState();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const { data, loading, reload } = useAsync(
    () => faqRepo.list(orgId, { page: tableState.page, perPage: tableState.perPage, search: tableState.search }),
    [orgId, tableState.page, tableState.perPage, tableState.search]
  );

  const save = async () => {
    const question = form.question.trim();
    const answer = form.answer.trim();
    if (!question || !answer) return toast(tx("กรอกคำถามและคำตอบ", "Question and answer required"), "error");
    setSaving(true);
    try {
      const payload = { question, answer, category: form.category.trim() };
      if (form.id) await faqRepo.update(orgId, form.id, payload);
      else await faqRepo.create(orgId, payload);
      setForm(null);
      reload();
      toast(tx("บันทึกคำถามแล้ว", "FAQ saved"), "success");
    } catch (error) {
      toast(error?.message || tx("บันทึกไม่สำเร็จ", "Save failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(tx("ลบคำถามนี้?", "Delete this FAQ?"))) return;
    try {
      await faqRepo.remove(orgId, row.id);
      reload();
      toast(tx("ลบคำถามแล้ว", "FAQ deleted"), "success");
    } catch (error) {
      toast(error?.message || tx("ลบไม่สำเร็จ", "Delete failed"), "error");
    }
  };

  return (
    <>
      <PageHead
        title={tx("คำถามที่พบบ่อย (FAQ)", "FAQ")}
        subtitle={tx("แสดงให้ลูกค้าในศูนย์ช่วยเหลือ", "Shown to customers in the Help Center")}
        actions={<Button variant="primary" onClick={() => setForm(EMPTY)}>+ {tx("เพิ่มคำถาม", "Add FAQ")}</Button>}
      />

      <div className="filter-bar">
        <input className="input-crm" style={{ maxWidth: 260 }} placeholder={tx("ค้นหา", "Search")} value={tableState.search} onChange={(e) => tableState.setSearch(e.target.value)} />
      </div>

      <DataTable
        loading={loading}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        emptyText={tx("ยังไม่มีคำถามที่พบบ่อย", "No FAQs yet")}
        columns={[
          { key: "question", label: tx("คำถาม", "Question") },
          { key: "category", label: tx("หมวดหมู่", "Category"), render: (row) => (row.category ? <Badge tone="indigo">{row.category}</Badge> : "-") },
          {
            key: "action",
            label: "",
            render: (row) => (
              <>
                <Button size="sm" onClick={() => setForm({ ...EMPTY, ...row })}>{tx("แก้ไข", "Edit")}</Button>{" "}
                <Button size="sm" onClick={() => remove(row)}>{tx("ลบ", "Delete")}</Button>
              </>
            ),
          },
        ]}
      />

      <Modal
        open={!!form}
        title={form?.id ? tx("แก้ไขคำถาม", "Edit FAQ") : tx("เพิ่มคำถาม", "New FAQ")}
        onClose={() => setForm(null)}
        footer={<><Button onClick={() => setForm(null)}>{tx("ยกเลิก", "Cancel")}</Button><Button variant="primary" disabled={saving} onClick={save}>{tx("บันทึก", "Save")}</Button></>}
      >
        {form ? (
          <>
            <Input label={tx("คำถาม", "Question")} value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} />
            <Textarea label={tx("คำตอบ", "Answer")} rows={5} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} />
            <Input label={tx("หมวดหมู่ (ไม่บังคับ)", "Category (optional)")} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
          </>
        ) : null}
      </Modal>
    </>
  );
}
